import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useScrollPercent from '@/hooks/useScrollPercent';
import { toSectionId } from './utils';
import styles from './ArticleNav.module.scss';

interface Props {
  markdown: string;
}

const ArticleNav = ({ markdown }: Props) => {
  const { t } = useTranslation();
  const scrollPercent = useScrollPercent();

  const headings = useMemo(() => [...markdown.matchAll(/^##\s+(.+)$/gm)].map((m) => m[1].trim()), [markdown]);

  const [activeId, setActiveId] = useState<string | null>(() =>
    headings.length > 0 ? toSectionId(headings[0]) : null
  );

  useEffect(() => {
    if (headings.length === 0) return;

    const updateActive = () => {
      const middle = window.innerHeight / 2;
      // Last heading whose top is at or above the viewport middle
      let next = toSectionId(headings[0]);
      for (const heading of headings) {
        const el = document.getElementById(toSectionId(heading));
        if (!el) continue;
        if (el.getBoundingClientRect().top <= middle) next = toSectionId(heading);
        else break;
      }
      setActiveId(next);
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    return () => window.removeEventListener('scroll', updateActive);
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav className={styles.nav} aria-label={t('blog.articleNav')}>
      <div className={styles.track}>
        {headings.map((heading) => {
          const id = toSectionId(heading);
          const isActive = id === activeId;
          return (
            <a
              key={id}
              href={`#${id}`}
              className={`${styles.dotLink}${isActive ? ` ${styles.active}` : ''}`}
              aria-label={heading}
              aria-current={isActive ? 'true' : undefined}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <span className={styles.label}>{heading}</span>
              <span className={styles.dot} />
            </a>
          );
        })}
      </div>
      <span className={styles.percent} aria-hidden='true'>
        {scrollPercent}%
      </span>
    </nav>
  );
};

export default ArticleNav;
